<template>
	<div
		class="pronto-phone"
		:class="{ large, disabled }"
		v-on-click-outside="closeCountrySelector"
	>
		<pronto-phone-number-input
			v-bind="inputProps"
			color="#e4e6e8"
			valid-color="#e4e6e8"
			error-color="#e06477"
			:error="error"
			:translations="localizations"
			:border-radius="6"
			:disabled="disabled"
			v-model="value"
			defaultCountryCode="US"
			@keyup.enter="$emit('enter', $event)"
			:class="{ 'error-border-color-country': error }"
			ref="phone"
			noSearch
			noExample
			@update="onUpdate"
			size="lg"
		>
			<template #arrow>
				<ChevronIcon class="chevron" />
			</template>
		</pronto-phone-number-input>
		<div class="after">
			<slot name="after" />
		</div>
	</div>
</template>

<script>
import { ChevronIcon } from '@/icons'

export default {
	name: 'PhoneInput',
	components: { ChevronIcon },
	props: {
		modelValue: null,
		inputProps: Object,
		error: { type: Boolean, default: null },
		autoFocus: { type: Boolean, default: null },
		large: { type: Boolean, default: null },
		disabled: { type: Boolean, default: null },
		placeholder: String,
		translations: Object,
	},
	emits: ['update:modelValue', 'change', 'enter'],
	inject: ['labelId'],
	data() {
		return { value: this.modelValue, results: {} }
	},

	mounted() {
		const phone_selector = document.querySelector(
			'#MazPhoneNumberInput-14_country_selector'
		)

		if (phone_selector) {
			phone_selector.tabIndex = -1
		}

		if (this.telInput) {
			if (this.labelId) {
				this.telInput.id = this.labelId
			}
			if (this.autoFocus) {
				this.telInput.focus()
			}
		}
	},

	watch: {
		modelValue(modelValue) {
			// if prop value changes, update internal value model
			this.value = modelValue
		},
	},

	computed: {
		telInput() {
			return this.$refs.phone?.$refs?.PhoneNumberInput?.$refs?.input ?? null // 🤢
		},
		localizations() {
			return {
				phoneNumberLabel: this.placeholder || '',
				...(this.translations || {}),
			}
		},
	},

	methods: {
		closeCountrySelector() {
			if (!this.$refs.phone) return
			this.$refs.phone.$refs.CountrySelector.hasListOpen = false
		},
		isValid() {
			if (!this.$refs.phone.modelValue) return true
			return this.results.isValid
		},
		onUpdate(results) {
			this.results = results
			this.$emit(
				'update:modelValue',
				this.results.isValid ? this.results.e164 : null
			)
			this.$emit('change', this.results.isValid ? this.results.e164 : null)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$large-height: 60px;

.pronto-phone {
	position: relative;

	&.large {
		:deep(.m-phone-number-input) {
			.m-phone-number-input__input {
				height: $large-height;
			}
			.m-phone-number-input__select {
				height: $large-height;
			}
			.m-phone-number-input__country-flag {
				top: 25px;
			}
			.m-select-input__toggle-button {
				top: calc(50% - 7px);
			}
			.m-phone-number-input__input,
			.m-phone-number-input__select {
				font-size: 14px;
				font-family: 'Avenir-Medium';
				color: var(--neutral-800);
			}
		}
	}

	.after {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		@include flex;
		z-index: 5;
	}

	:deep(.m-phone-number-input) {
		.vue-recycle-scroller.ready .vue-recycle-scroller__item-view {
			position: unset;
		}

		.m-phone-number-input__select {
			@media screen and (max-width: 580px) {
				flex: 0 0 90px;
				width: 90px;
				min-width: 90px;
				max-width: 90px;
			}
		}

		.m-phone-number-input__country-flag {
			top: 15px;
		}

		.m-input-wrapper {
			border-color: $outline-gray !important;
			color: var(--neutral-800);
			background: #fafafa !important;
			border: 1px solid;
		}

		.m-input-wrapper-input {
			.m-select-input {
				border-color: $outline-gray;
				caret-color: $text-gray-dark !important;
				cursor: unset;
				background: #fafafa;
				&::-webkit-input-placeholder {
					color: var(--neutral-400);
				}
				&::-moz-placeholder {
					color: var(--neutral-400);
				}

				.m-input-label {
					color: var(--neutral-400) !important;
				}
			}
		}

		.m-select-input__toggle-button .maz-custom {
			font-size: 14px;
			color: var(--neutral-400);
			top: calc(50% - 8px);
			right: 12px;
		}

		.m-select-list-item {
			&.--is-selected {
				color: unset;
			}

			&:hover {
				background-color: rgb(228, 230, 232);
			}
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.pronto-phone {
		.m-phone-number-input {
			.m-input-wrapper {
				background: var(--neutral-100) !important;
				border-color: var(--neutral-100) !important;
			}

			.m-phone-number-input__input .m-input-wrapper {
				border-left-color: var(--neutral-400) !important;
			}

			.m-input-wrapper-input {
				.m-select-input {
					background-color: var(--neutral-100) !important;
					color: var(--neutral-800) !important;
					caret-color: var(--neutral-800) !important;
				}
			}

			.m-select-chevron {
				color: var(--neutral-800) !important;
			}

			.m-select-list {
				background-color: var(--neutral-100) !important;
				border-color: var(--neutral-100) !important;

				.m-select-list-item {
					color: var(--neutral-800) !important;

					&:hover,
					&.--is-selected {
						background-color: var(--neutral-200) !important;
					}
				}
			}

			input:-webkit-autofill,
			input:-webkit-autofill:hover,
			input:-webkit-autofill:focus,
			input:-webkit-autofill:active {
				-webkit-box-shadow: 0 0 0 30px var(--neutral-100) inset !important;
				-webkit-text-fill-color: var(--neutral-800) !important;
			}
		}
	}
}
</style>
